import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { PLANS, BUSINESS_TYPES } from '../lib/constants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Building2,
  Phone,
  Bot,
  Pause,
  Play,
  Key,
  Copy,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Snowflake,
  RotateCcw,
  Users,
  Zap,
  Sparkles,
  Shield,
  ShieldCheck,
  Clock,
  Smartphone,
  BarChart3,
  Database,
  Save
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fluxoco_token';



const STATUS_CONFIG = {
  active: { label: 'Activo', icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
  paused: { label: 'Pausado', icon: Pause, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
  suspended: { label: 'Suspendido', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' }
};

const TenantsPage = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCredentialsOpen, setIsCredentialsOpen] = useState(false);
  const [isSilosOpen, setIsSilosOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [silos, setSilos] = useState([]);
  const [loadingSilos, setLoadingSilos] = useState(false);
  const [newSilo, setNewSilo] = useState({ name: '', display_name: '' });
  const [formData, setFormData] = useState({
    name: '',
    whatsapp_principal: '',
    admin_email: '',
    plan: 'demo',
    business_type: 'otro',
    description: '',
    logo_url: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => { 
    fetchTenants(); 
    fetchVerticals();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/tenants`, { headers: getAuthHeaders() });
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Error al cargar los tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchVerticals = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/verticals`, { headers: getAuthHeaders() });
      setVerticals(response.data);
    } catch (error) {
      console.error('Error fetching verticals:', error);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.whatsapp_principal || !formData.admin_email) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/admin/tenants`, formData, { headers: getAuthHeaders() });
      toast.success(`Tenant "${formData.name}" creado exitosamente`);
      setIsCreateOpen(false);
      resetForm();
      fetchTenants();
      setCredentials({ ...response.data, tenant_name: response.data.name });
      setIsCredentialsOpen(true);
    } catch (error) {
      console.error('Error creating tenant:', error);
      toast.error('Error al crear el tenant');
    }
  };

  const handleUpdate = async () => {
    if (!selectedTenant) return;
    try {
      await axios.put(`${API_URL}/api/admin/tenants/${selectedTenant.id}`, formData, { headers: getAuthHeaders() });
      toast.success('Tenant actualizado exitosamente');
      setIsEditOpen(false);
      setSelectedTenant(null);
      resetForm();
      fetchTenants();
    } catch (error) {
      toast.error('Error al actualizar el tenant');
    }
  };

  const handleDelete = async (tenant) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${tenant.name}"? Esta acción eliminará permanentemente todos los datos, colecciones (silos), bots y conversaciones asociados.`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/tenants/${tenant.id}`, { headers: getAuthHeaders() });
      toast.success(`Tenant "${tenant.name}" eliminado correctamente (limpieza en cascada exitosa)`);
      fetchTenants();
    } catch (error) {
      if (error.response?.status === 404) {
        // En caso de que se haya borrado en una petición previa (doble click), se considera éxito
        toast.success(`Tenant "${tenant.name}" eliminado`);
        fetchTenants();
      } else {
        console.error("Error deleting tenant:", error);
        toast.error('Error al eliminar el tenant');
      }
    }
  };

  const handleToggleStatus = async (tenant) => {
    try {
      const response = await axios.patch(`${API_URL}/api/admin/tenants/${tenant.id}/status`, {
        status: tenant.status === 'active' ? 'paused' : 'active'
      }, { headers: getAuthHeaders() });
      toast.success(response.data.message);
      fetchTenants();
    } catch (error) {
      toast.error('Error al cambiar el estado');
    }
  };

  const fetchCredentials = async (tenantId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/tenants/${tenantId}/credentials`, { headers: getAuthHeaders() });
      setCredentials(response.data);
      setIsCredentialsOpen(true);
    } catch (error) {
      toast.error('Error al obtener credenciales');
    }
  };

  const openSilosDialog = async (tenant) => {
    setSelectedTenant(tenant);
    setIsSilosOpen(true);
    setLoadingSilos(true);
    try {
      const response = await axios.get(`${API_URL}/api/tenants/${tenant.id}/collections`, { headers: getAuthHeaders() });
      const collectionsArray = Object.entries(response.data).map(([key, config]) => ({ id: key, ...config }));
      setSilos(collectionsArray);
    } catch (error) {
      toast.error('Error al cargar silos');
    } finally {
      setLoadingSilos(false);
    }
  };

  const handleCreateSilo = async () => {
    if (!newSilo.name || !newSilo.display_name) {
      toast.error('Por favor completa el nombre del silo');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/tenants/${selectedTenant.id}/collections`, newSilo, { headers: getAuthHeaders() });
      toast.success(`Silo "${newSilo.display_name}" creado`);
      setNewSilo({ name: '', display_name: '' });
      openSilosDialog(selectedTenant);
    } catch (error) {
      toast.error('Error al crear el silo');
    }
  };

  const handleDeleteSilo = async (siloId) => {
    try {
        const response = await axios.delete(`${API_URL}/tenants/${selectedTenant.id}/collections/${siloId}`);
        toast.success(response.data.message || `Silo ${siloId} eliminado`);
        fetchSilos(selectedTenant.id);
    } catch (error) {
        console.error('Error al borrar silo:', error);
        const errorMsg = error.response?.data?.detail || 'No se pudo eliminar el silo';
        toast.error(`ERROR: ${errorMsg}`);
    }
  };

  const handleActivateKit = async () => {
    if (!window.confirm('¿Activar el Kit Universal 5x5 (Catálogos, Ventas, Tickets, Citas y CRM) para este tenant?')) return;
    try {
      await axios.post(`${API_URL}/api/tenants/${selectedTenant.id}/collections/activate-kit`, {}, { headers: getAuthHeaders() });
      toast.success('¡Kit Universal 5x5 Activado!');
      openSilosDialog(selectedTenant);
    } catch (error) {
      toast.error('Error al activar el kit');
    }
  };

  const handleResetPassword = async (tenantId) => {
    if (!window.confirm('¿Estás seguro de restablecer la contraseña?')) return;
    try {
      const response = await axios.post(`${API_URL}/api/admin/tenants/${tenantId}/reset-password`, {}, { headers: getAuthHeaders() });
      toast.success('Contraseña restablecida correctamente');
      setCredentials(response.data);
      setIsCredentialsOpen(true);
    } catch (error) {
      toast.error('Error al restablecer la contraseña');
    }
  };

  const openEdit = (tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name,
      whatsapp_principal: tenant.whatsapp_principal,
      admin_email: tenant.admin_email,
      plan: tenant.plan,
      business_type: tenant.vertical_id || tenant.business_type || 'otro',
      description: tenant.description || '',
      logo_url: tenant.logo_url || ''
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      whatsapp_principal: '', 
      admin_email: '', 
      plan: 'demo', 
      business_type: 'otro', 
      description: '', 
      logo_url: ''
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          tenant.admin_email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlan = filterPlan === 'all' || tenant.plan === filterPlan;
    const matchesStatus = filterStatus === 'all' || tenant.status === filterStatus;
    return matchesSearch && matchesPlan && matchesStatus;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // ==== MIDNIGHT GLASS FORM ====
  const renderTenantForm = (isEdit = false) => (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2 col-span-2">
          <Label className="text-[11px] text-slate-400 uppercase tracking-widest font-bold">Nombre del Tenant *</Label>
          <Input
            data-testid="tenant-name-input"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ej: Distribuidora ABC"
            className="bg-slate-950/50 border-white/10 rounded-xl h-11 text-white placeholder:text-slate-600 focus:border-blue-500/50 focus:ring-blue-500/20"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[11px] text-slate-400 uppercase tracking-widest font-bold">WhatsApp *</Label>
          <Input
            data-testid="tenant-whatsapp-input"
            value={formData.whatsapp_principal}
            onChange={(e) => setFormData({ ...formData, whatsapp_principal: e.target.value })}
            placeholder="+52 55 1234 5678"
            className="bg-slate-950/50 border-white/10 rounded-xl h-11 text-white placeholder:text-slate-600 focus:border-blue-500/50"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[11px] text-slate-400 uppercase tracking-widest font-bold">Email Admin *</Label>
          <Input
            data-testid="tenant-email-input"
            type="email"
            value={formData.admin_email}
            onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
            placeholder="admin@empresa.com"
            className="bg-slate-950/50 border-white/10 rounded-xl h-11 text-white placeholder:text-slate-600 focus:border-blue-500/50"
          />
        </div>
        <div className="space-y-2 col-span-2">
          <Label className="text-[11px] text-slate-400 uppercase tracking-widest font-bold">Plan</Label>
          <Select value={formData.plan} onValueChange={(value) => setFormData({ ...formData, plan: value })}>
            <SelectTrigger data-testid="tenant-plan-select" className="bg-slate-950/50 border-white/10 rounded-xl h-11 text-white">
              <SelectValue placeholder="Selecciona un plan" />
            </SelectTrigger>
            <SelectContent className="bg-slate-950 border-white/10 rounded-xl">
              {PLANS.map((plan) => (
                <SelectItem key={plan.value} value={plan.value}>{plan.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 col-span-2">
          <Label className="text-[11px] text-slate-400 uppercase tracking-widest font-bold">Giro del Negocio</Label>
          <Select value={formData.business_type} onValueChange={(value) => setFormData({ ...formData, business_type: value })}>
            <SelectTrigger className="bg-slate-950/50 border-white/10 rounded-xl h-11 text-white">
              <SelectValue placeholder="Selecciona un giro" />
            </SelectTrigger>
            <SelectContent className="bg-slate-950 border-white/10 rounded-xl">
              {(verticals.length > 0 ? verticals : BUSINESS_TYPES).map((type) => (
                <SelectItem key={type.id || type.value} value={type.id || type.value}>
                  {type.label} {type.layer === 'base' ? '(Base)' : type.layer === 'operativo' ? '(Operativo)' : type.layer === 'especializado' ? '(Especializado)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 col-span-2">
          <Label className="text-[11px] text-slate-400 uppercase tracking-widest font-bold">Descripción</Label>
          <Textarea
            data-testid="tenant-description-input"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Descripción breve del negocio..."
            rows={2}
            className="bg-slate-950/50 border-white/10 rounded-xl text-white placeholder:text-slate-600 focus:border-blue-500/50"
          />
        </div>
      </div>

      <div className="pt-10">
        <button 
          onClick={isEdit ? handleUpdate : handleCreate}
          className="group relative overflow-hidden w-full h-16 bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase tracking-[0.3em] rounded-2xl transition-all flex items-center justify-center gap-3 shadow-xl shadow-blue-500/20 active:scale-[0.98]"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/10 to-blue-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
          <Save className="w-5 h-5 relative z-10 group-hover:scale-110 transition-transform" /> 
          <span className="relative z-10">{isEdit ? 'Actualizar Tenant' : 'Crear Acceso Maestro'}</span>
        </button>
      </div>
    </div>
  );

  // ==== STAT CARDS DATA ====
  const statsData = [
    { label: 'Total Tenants', value: tenants.length, icon: Building2, gradient: 'from-blue-600/20 to-indigo-600/20', borderColor: 'border-blue-500/20', iconColor: 'text-blue-400' },
    { label: 'Activos', value: tenants.filter(t => t.status === 'active').length, icon: CheckCircle2, gradient: 'from-emerald-600/20 to-green-600/20', borderColor: 'border-emerald-500/20', iconColor: 'text-emerald-400' },
    { label: 'Plan Pro', value: tenants.filter(t => t.plan === 'pro').length, icon: Users, gradient: 'from-cyan-600/20 to-blue-600/20', borderColor: 'border-cyan-500/20', iconColor: 'text-cyan-400' },
    { label: 'Enterprise', value: tenants.filter(t => t.plan === 'enterprise').length, icon: Zap, gradient: 'from-amber-600/20 to-orange-600/20', borderColor: 'border-amber-500/20', iconColor: 'text-amber-400' },
    { label: 'Total Bots', value: tenants.reduce((acc, t) => acc + (t.bots_count || 0), 0), icon: Bot, gradient: 'from-purple-600/20 to-pink-600/20', borderColor: 'border-purple-500/20', iconColor: 'text-purple-400' },
  ];

  // ==== LOADING SKELETON ====
  if (loading) {
    return (
      <div className="space-y-8">
        <div className="h-12 w-64 bg-slate-800/50 animate-pulse rounded-2xl" />
        <div className="grid grid-cols-5 gap-4">
          {[1,2,3,4,5].map(i => <div key={i} className="h-28 bg-slate-800/30 animate-pulse rounded-[2rem]" />)}
        </div>
        <div className="h-96 rounded-[2rem] bg-slate-800/20 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="tenants-page">

      {/* ===== HERO HEADER ===== */}
      {/* Header V4.3 - ADN Fluxo Co */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 pt-4">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-blue-500" />
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-blue-500/80">Gestión de Clientes</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit">
            Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Tenants</span>
          </h1>
          <p className="text-slate-500 mt-2 font-medium italic text-sm">Administra los clientes conectados a tu red de automatización inteligente</p>
        </motion.div>

        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-tenant-btn" className="group relative overflow-hidden rounded-2xl bg-blue-600 hover:bg-blue-500 text-white px-6 h-12 shadow-xl shadow-blue-500/20 transition-all duration-300 hover:shadow-blue-500/30 hover:scale-[1.02]">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/10 to-blue-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              <Plus className="w-5 h-5 mr-2" />
              <span className="font-bold">Crear Nuevo Tenant</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl bg-[#0B1120] border-blue-500/20 rounded-[3rem] p-0 overflow-hidden outline-none shadow-2xl">
            <div className="bg-gradient-to-br from-blue-500/10 to-transparent p-10 max-h-[90vh] overflow-y-auto custom-scrollbar">
              <DialogHeader>
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20 shadow-inner">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <DialogTitle className="font-outfit text-3xl font-black text-white italic tracking-tighter uppercase">Crear Nuevo Tenant</DialogTitle>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Gestión de Clientes Fluxo Co</p>
                  </div>
                </div>
              </DialogHeader>
              {renderTenantForm()}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* ===== FILTERS BAR ===== */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-col sm:flex-row gap-4 p-5 rounded-[2rem] bg-[#0F172A]/40 backdrop-blur-xl border border-white/5"
      >
        <div className="relative flex-1 max-w-md group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
          <Input
            data-testid="tenant-search-input"
            placeholder="Buscar tenants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-11 bg-slate-950/50 border-white/5 rounded-xl h-11 text-white placeholder:text-slate-600 focus:border-emerald-500/40 focus:ring-0 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all duration-300"
          />
        </div>
        <Select value={filterPlan} onValueChange={setFilterPlan}>
          <SelectTrigger className="w-40 bg-slate-950/50 border-white/5 rounded-xl h-11 text-white">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent className="bg-slate-950 border-white/10 rounded-xl">
            <SelectItem value="all">Todos los planes</SelectItem>
            {PLANS.map((plan) => (
              <SelectItem key={plan.value} value={plan.value}>{plan.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 bg-slate-950/50 border-white/5 rounded-xl h-11 text-white">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent className="bg-slate-950 border-white/10 rounded-xl">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="paused">Pausados</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      {/* ===== STAT CARDS — MIDNIGHT GLASS ===== */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {statsData.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.07 }}
            className={`group relative overflow-hidden rounded-[2rem] bg-gradient-to-br ${stat.gradient} backdrop-blur-xl border ${stat.borderColor} p-6 cursor-default hover:scale-[1.03] transition-all duration-500`}
          >
            {/* Corner glow */}
            <div className={`absolute -top-8 -right-8 w-20 h-20 ${stat.iconColor.replace('text-', 'bg-').replace('400', '500')}/10 blur-2xl rounded-full group-hover:scale-150 transition-transform duration-700`} />

            <div className="relative z-10 flex items-center justify-between">
              <div>
                <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest">{stat.label}</p>
                <p className="text-3xl font-black text-white mt-1">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* ===== TENANTS TABLE — MIDNIGHT GLASS ===== */}
      {filteredTenants.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-20 rounded-[2rem] bg-[#0F172A]/30 backdrop-blur-xl border border-white/5">
          <div className="w-16 h-16 rounded-2xl bg-slate-800/30 flex items-center justify-center mb-4">
            <Building2 className="w-8 h-8 text-slate-600" />
          </div>
          <h3 className="text-lg font-bold text-white">No hay tenants</h3>
          <p className="text-slate-500 text-sm">{searchQuery ? 'No se encontraron resultados' : 'Crea tu primer tenant para comenzar'}</p>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-[2rem] bg-[#0F172A]/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/20"
        >
          {/* Table Header */}
          <div className="grid grid-cols-[2fr_1.5fr_0.8fr_0.8fr_0.5fr_1fr_0.5fr] gap-4 px-8 py-5 border-b border-white/5">
            {['Tenant', 'WhatsApp', 'Plan', 'Estado', 'Bots', 'Fecha Alta', ''].map(col => (
              <span key={col} className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{col}</span>
            ))}
          </div>

          {/* Table Rows */}
          {filteredTenants.map((tenant, idx) => {
            const statusConfig = STATUS_CONFIG[tenant.status] || STATUS_CONFIG.active;
            const StatusIcon = statusConfig.icon;
            const plan = PLANS.find(p => p.value === tenant.plan);

            return (
              <motion.div
                key={tenant.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                data-testid={`tenant-row-${tenant.id}`}
                className="grid grid-cols-[2fr_1.5fr_0.8fr_0.8fr_0.5fr_1fr_0.5fr] gap-4 items-center px-8 py-5 border-b border-white/[0.03] hover:bg-white/[0.02] transition-all duration-300 group"
              >
                {/* Tenant Info */}
                <div className="flex items-center gap-4">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-lg bg-gradient-to-br from-violet-500 to-purple-600 shadow-violet-500/20 group-hover:scale-110 transition-transform duration-300`}>
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                        <p className="font-bold text-white text-sm group-hover:text-blue-400 transition-colors">{tenant.name}</p>
                    </div>
                    <p className="text-[11px] text-slate-500 font-mono">{tenant.admin_email}</p>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-slate-600" />
                  <span className="text-sm text-slate-300 font-mono">{tenant.whatsapp_principal}</span>
                </div>

                {/* Plan Badge */}
                <div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider text-white ${plan?.color || 'bg-slate-600'} shadow-sm`}>
                    {plan?.label || tenant.plan}
                  </span>
                </div>

                {/* Status */}
                <div>
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${statusConfig.bg}`}>
                    <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.color}`} />
                    <span className={`text-[10px] font-bold ${statusConfig.color}`}>{statusConfig.label}</span>
                  </div>
                </div>

                {/* Bots */}
                <div className="flex items-center gap-1.5">
                  <Bot className="w-3.5 h-3.5 text-slate-600" />
                  <span className="text-sm text-white font-bold">{tenant.bots_count || 0}</span>
                </div>

                {/* Date */}
                <div className="flex items-center gap-1.5 text-slate-500">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-[11px] font-medium">{formatDate(tenant.created_at)}</span>
                </div>

                {/* Actions */}
                <div className="flex justify-end">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`tenant-menu-${tenant.id}`} className="rounded-xl text-slate-500 hover:text-white hover:bg-white/5 w-9 h-9">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#0B1120] border-white/10 rounded-xl p-1.5 shadow-2xl shadow-black/50 min-w-[200px]">
                      <DropdownMenuItem onClick={() => window.open(`/tenant/${tenant.db_prefix}`, '_blank')} className="rounded-lg hover:bg-white/5 cursor-pointer py-2.5">
                        <ExternalLink className="w-4 h-4 mr-3 text-blue-400" />
                        <span className="text-sm">Ir al Portal</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => navigate(`/dashboard/${tenant.id}`)} 
                        className="rounded-lg hover:bg-white/5 cursor-pointer py-2.5"
                      >
                        <BarChart3 className="w-4 h-4 mr-3 text-emerald-400" />
                        <span className="text-sm">Panel de Control</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem 
                        onClick={() => navigate(`/bots?tenant_id=${tenant.id}&tenant_name=${encodeURIComponent(tenant.name)}&business_type=${tenant.business_type || 'otro'}`)} 
                        className="rounded-lg hover:bg-white/5 cursor-pointer py-2.5"
                      >
                        <Bot className="w-4 h-4 mr-3 text-cyan-400" />
                        <span className="text-sm font-bold text-cyan-400">Crear Bot Relacionado</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEdit(tenant)} className="rounded-lg hover:bg-white/5 cursor-pointer py-2.5">
                        <Edit className="w-4 h-4 mr-3 text-slate-400" />
                        <span className="text-sm">Editar</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => fetchCredentials(tenant.id)} className="rounded-lg hover:bg-white/5 cursor-pointer py-2.5">
                        <Key className="w-4 h-4 mr-3 text-amber-400" />
                        <span className="text-sm">Ver Credenciales</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleToggleStatus(tenant)} className="rounded-lg hover:bg-white/5 cursor-pointer py-2.5">
                        {tenant.status === 'active' ? (
                          <><Pause className="w-4 h-4 mr-3 text-amber-400" /><span className="text-sm">Pausar</span></>
                        ) : (
                          <><Play className="w-4 h-4 mr-3 text-emerald-400" /><span className="text-sm">Activar</span></>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleResetPassword(tenant.id)} className="rounded-lg hover:bg-white/5 cursor-pointer py-2.5">
                        <RotateCcw className="w-4 h-4 mr-3 text-slate-400" />
                        <span className="text-sm">Restablecer Contraseña</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-white/5" />
                      <DropdownMenuItem onClick={() => handleDelete(tenant)} className="rounded-lg hover:bg-red-500/10 cursor-pointer py-2.5 text-red-400 focus:text-red-400">
                        <Trash2 className="w-4 h-4 mr-3" />
                        <span className="text-sm">Eliminar</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* ===== EDIT DIALOG ===== */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-xl bg-[#0B1120] border-blue-500/20 rounded-[3rem] p-0 overflow-hidden outline-none shadow-2xl">
          <div className="bg-gradient-to-br from-blue-500/10 to-transparent p-10 max-h-[90vh] overflow-y-auto custom-scrollbar">
            <DialogHeader>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20 shadow-inner">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <DialogTitle className="font-outfit text-3xl font-black text-white italic tracking-tighter uppercase">Editar Tenant</DialogTitle>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Actualización de Registro Maestro</p>
                </div>
              </div>
            </DialogHeader>
            {renderTenantForm(true)}
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== CREDENTIALS DIALOG ===== */}
      <Dialog open={isCredentialsOpen} onOpenChange={setIsCredentialsOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-none bg-transparent">
          <div className="bg-[#0B1120] border border-white/10 rounded-[2rem] shadow-2xl shadow-black/50 overflow-hidden">
            <div className="p-6 border-b border-white/5 bg-white/[0.02]">
              <h2 className="text-xl font-bold font-outfit text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <Key className="w-5 h-5 text-amber-400" />
                </div>
                Credenciales de Acceso
              </h2>
              <p className="text-[11px] text-slate-500 mt-2 ml-[52px]">Guarda estos datos en un lugar seguro.</p>
            </div>

            {credentials && (
              <div className="p-6 space-y-5">
                <div className="space-y-1">
                  <span className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-black">Negocio</span>
                  <p className="font-bold text-lg text-white">{credentials.tenant_name}</p>
                </div>

                <div className="space-y-2">
                  <span className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-black">Email Admin</span>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-950/50 p-3 rounded-xl border border-white/5 font-mono text-sm text-white break-all">
                      {credentials.admin_email}
                    </div>
                    <Button size="icon" variant="outline" className="h-11 w-11 shrink-0 rounded-xl border-white/10 hover:bg-white/5" onClick={() => copyToClipboard(credentials.admin_email)}>
                      <Copy className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                </div>

                {(credentials.admin_password || credentials.new_password) && (
                  <div className="space-y-2">
                    <span className="text-[10px] text-amber-400 uppercase tracking-[0.2em] font-black">Contraseña Provisional</span>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-amber-500/10 p-3 rounded-xl border border-amber-500/20 font-mono text-base font-bold text-amber-400">
                        {credentials.new_password || credentials.admin_password}
                      </div>
                      <Button size="icon" variant="outline" className="h-11 w-11 shrink-0 rounded-xl border-amber-500/20 hover:bg-amber-500/10" onClick={() => copyToClipboard(credentials.new_password || credentials.admin_password)}>
                        <Copy className="w-4 h-4 text-amber-400" />
                      </Button>
                    </div>
                    <div className="p-3 bg-amber-500/5 rounded-xl border border-amber-500/10">
                      <p className="text-[10px] text-amber-400/80 leading-relaxed">⚠️ <strong>Importante:</strong> Esta contraseña solo se muestra una vez.</p>
                    </div>
                  </div>
                )}

                {!credentials.new_password && !credentials.admin_password && (
                  <div className="bg-blue-500/5 p-4 rounded-xl border border-blue-500/10 text-[11px] text-blue-400 leading-relaxed">
                    La contraseña está encriptada. Usa <strong>"Restablecer Contraseña"</strong> para generar una nueva.
                  </div>
                )}

                <div className="space-y-2">
                  <span className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-black">URL de Acceso</span>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-950/50 p-3 rounded-xl border border-white/5 font-mono text-xs text-blue-400 overflow-hidden text-ellipsis whitespace-nowrap">
                      {credentials.access_url}
                    </div>
                    <Button size="icon" variant="outline" className="h-11 w-11 shrink-0 rounded-xl border-white/10 hover:bg-white/5" onClick={() => copyToClipboard(`${window.location.origin}${credentials.access_url}`)}>
                      <Copy className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                </div>

                <Button className="w-full h-12 rounded-xl text-base font-bold bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-500/20" onClick={() => setIsCredentialsOpen(false)}>
                  Entendido y Copiado
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* DIÁLOGOS MIGRADOS (Ahora en Arquitectura Engine) */}
    </div>
  );
};

export default TenantsPage;
